import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { streamAI, createData } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FacebookLogo, InstagramLogo, TiktokLogo, ThreadsLogo, XLogo, LinkedinLogo, PinterestLogo,
  YoutubeLogo, GoogleLogo, Package, EnvelopeSimple, ChatText, MegaphoneSimple, Article, Tag,
  Snowflake, Gift, ChartBar, Coffee, Confetti, Sparkle, FloppyDisk, Copy, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = [
  { id: "facebook_post", label: "Facebook", icon: FacebookLogo, platform: "Facebook" },
  { id: "instagram_post", label: "Instagram", icon: InstagramLogo, platform: "Instagram" },
  { id: "threads_post", label: "Threads", icon: ThreadsLogo, platform: "Threads" },
  { id: "x_post", label: "X / Twitter", icon: XLogo, platform: "X" },
  { id: "linkedin_post", label: "LinkedIn", icon: LinkedinLogo, platform: "LinkedIn" },
  { id: "pinterest_post", label: "Pinterest", icon: PinterestLogo, platform: "Pinterest" },
  { id: "tiktok_caption", label: "TikTok", icon: TiktokLogo, platform: "TikTok" },
  { id: "gbp_post", label: "Google Business", icon: GoogleLogo, platform: "Google Business" },
  { id: "youtube_short", label: "YouTube Short", icon: YoutubeLogo, platform: "YouTube" },
  { id: "product_description", label: "Product Desc", icon: Package, platform: "Product" },
  { id: "email_campaign", label: "Email", icon: EnvelopeSimple, platform: "Email" },
  { id: "sms_campaign", label: "SMS", icon: ChatText, platform: "SMS" },
  { id: "google_ad", label: "Google Ad", icon: GoogleLogo, platform: "Google Ads" },
  { id: "meta_ad", label: "Meta Ad", icon: MegaphoneSimple, platform: "Meta Ads" },
  { id: "blog_post", label: "Blog Article", icon: Article, platform: "Blog" },
  { id: "flash_sale", label: "Flash Sale", icon: Tag, platform: "Campaign" },
  { id: "giveaway", label: "Giveaway", icon: Gift, platform: "Campaign" },
  { id: "poll", label: "Poll", icon: ChartBar, platform: "Social" },
  { id: "engagement_post", label: "Engagement", icon: Confetti, platform: "Social" },
  { id: "coffee_education", label: "Coffee Edu", icon: Coffee, platform: "Social" },
  { id: "brewing_tip", label: "Brewing Tip", icon: Coffee, platform: "Social" },
  { id: "flavor_spotlight", label: "Flavor Spotlight", icon: Sparkle, platform: "Campaign" },
  { id: "product_launch", label: "Product Launch", icon: Package, platform: "Campaign" },
  { id: "seasonal_idea", label: "Seasonal", icon: Snowflake, platform: "Campaign" },
];
const TONES = ["Bold & energetic", "Premium & refined", "Friendly & casual", "Urgent & promotional", "Educational"];

export default function AiStudioPage() {
  const { activeId } = useClient();
  const [type, setType] = useState("instagram_post");
  const [brief, setBrief] = useState("");
  const [tone, setTone] = useState("Bold & energetic");
  const [output, setOutput] = useState("");
  const [gen, setGen] = useState(false);

  const generate = async () => {
    if (!brief.trim()) return toast.error("Add a brief first");
    setGen(true); setOutput("");
    try {
      await streamAI("/ai/generate-stream", { client_id: activeId, content_type: type, brief, tone },
        (chunk) => setOutput((o) => o + chunk));
    } catch (e) {
      const m = e?.message || "Generation failed. Please try again.";
      setOutput(m);
      toast.error(m);
    }
    setGen(false);
  };

  const saveM = useMutation({
    mutationFn: () => createData("content_library", {
      client_id: activeId, title: brief.slice(0, 50) || "Generated content",
      type: TYPES.find((t) => t.id === type)?.label || "Content", platform: "All",
      status: "draft", body: output, tags: type,
    }),
    onSuccess: () => toast.success("Saved to Content Library"),
  });

  const approvalM = useMutation({
    mutationFn: () => {
      const t = TYPES.find((x) => x.id === type);
      return createData("approvals", {
        client_id: activeId, title: brief.slice(0, 50) || (t?.label || "Content"),
        type, platform: t?.platform || "Content", body: output, image_path: "",
        status: "pending", scheduled_at: "",
      });
    },
    onSuccess: () => toast.success("Sent to Approval Queue"),
  });

  const ActiveIcon = TYPES.find((t) => t.id === type)?.icon || Sparkle;

  return (
    <PageContainer>
      <PageHeader eyebrow="AI Content Studio" title="Generate On-Brand Content"
        subtitle="Pick a format, give a short brief, and let your AI marketing assistant draft copy in your brand voice." />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Controls */}
        <div className="lg:col-span-2 space-y-4">
          <div className="card-matte p-5">
            <Label className="label-caps">Content type</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {TYPES.map((t) => {
                const Icon = t.icon;
                const active = type === t.id;
                return (
                  <button key={t.id} data-testid={`type-${t.id}`} onClick={() => setType(t.id)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium border transition-all text-left ${active ? "border-[#6B3DFF]/40 bg-[#6B3DFF]/10 text-white" : "border-white/8 text-[#9a9a9a] hover:text-white hover:border-white/20"}`}>
                    <Icon size={16} className={active ? "brand-text" : ""} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="card-matte p-5 space-y-3">
            <div>
              <Label className="label-caps">Brief</Label>
              <Textarea data-testid="ai-brief" value={brief} onChange={(e) => setBrief(e.target.value)} rows={4}
                className="bg-[#10101A] border-white/10 mt-1" placeholder="e.g. Promote the Midnight Ember dark roast with a Subscribe & Save offer for busy professionals" />
            </div>
            <div>
              <Label className="label-caps">Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="bg-[#10101A] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14141F] border-white/10 text-white">
                  {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="generate-btn" onClick={generate} disabled={gen} className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
              <Sparkle size={16} className={`mr-1.5 ${gen ? "ai-thinking" : ""}`} weight="fill" /> {gen ? "Generating…" : "Generate with AI"}
            </Button>
          </div>
        </div>

        {/* Output */}
        <div className="lg:col-span-3">
          <div className="card-matte p-5 min-h-[420px] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><ActiveIcon size={18} className="brand-text" /><span className="font-head font-semibold">Output</span></div>
              {output && !gen && (
                <div className="flex gap-2">
                  <Button data-testid="copy-btn" size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(output); toast.success("Copied"); }} className="text-[#9a9a9a] hover:text-white"><Copy size={15} className="mr-1" /> Copy</Button>
                  <Button data-testid="save-library-btn" size="sm" onClick={() => saveM.mutate()} className="bg-white/10 hover:bg-white/15 text-white"><FloppyDisk size={15} className="mr-1" /> Save</Button>
                  <Button data-testid="to-approval-btn" size="sm" onClick={() => approvalM.mutate()} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><PaperPlaneTilt size={15} className="mr-1" /> To Approval</Button>
                </div>
              )}
            </div>
            <div data-testid="ai-output" className="flex-1 whitespace-pre-wrap text-sm text-[#e0e0e0] leading-relaxed overflow-y-auto">
              {output || <span className="text-[#5a5a5a]">Your AI-generated content will appear here…</span>}
              {gen && <span className="inline-block w-2 h-4 bg-[#6B3DFF] ml-0.5 ai-thinking align-middle" />}
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
